import { agentsApi } from '../../agents-api.ts';
import type { AgentInfo, AgentStatus } from '../../agents-api.ts';
import { badge, busy, button, toast } from '../../components.ts';
import { icon } from '../../icons.ts';
import { h } from '../../ui.ts';

/** Small pieces every agent screen shares: the status badge, the preview chip, the question card. */

const STATUS: Record<AgentStatus, { label: string; tone: string; pulse: boolean }> = {
  starting: { label: 'Starting', tone: 'accent', pulse: true },
  running: { label: 'Running', tone: 'accent', pulse: true },
  asking: { label: 'Needs you', tone: 'warning', pulse: false },
  idle: { label: 'Idle', tone: 'success', pulse: false },
  stopped: { label: 'Stopped', tone: 'outline', pulse: false },
  failed: { label: 'Failed', tone: 'danger', pulse: false },
};

export function statusBadge(status: AgentStatus): HTMLElement {
  const s = STATUS[status];
  return badge(
    s.tone,
    h('span', { class: `dot ${s.pulse ? 'pulse' : ''} ${status === 'stopped' ? 'idle' : ''}` }),
    s.label,
  );
}

/** The colour of a project's dot in the sidebar: the most urgent of its agents. */
export function statusTone(agents: AgentInfo[]): string {
  if (agents.some((a) => a.status === 'asking')) return 'warning';
  if (agents.some((a) => a.status === 'running' || a.status === 'starting')) return 'accent';
  if (agents.some((a) => a.status === 'failed')) return 'danger';
  return 'idle';
}

export function previewChip(a: AgentInfo): HTMLElement | null {
  const p = a.preview;
  if (p.state === 'ready' && p.url)
    return h(
      'a',
      {
        class: 'chip success',
        href: p.url,
        target: '_blank',
        rel: 'noopener',
        'aria-label': `Open the preview of ${a.title}`,
      },
      icon.globe(),
      'Preview',
      icon.open(12),
    );
  if (p.state === 'starting')
    return h('span', { class: 'chip' }, h('span', { class: 'dot pulse' }), 'Preview starting');
  if (p.state === 'failed') return h('span', { class: 'chip danger', title: p.error ?? '' }, 'Preview failed');
  return null;
}

export const branchLabel = (branch: string) => h('span', { class: 'branch' }, icon.branch(12), branch);

export const lastActivity = (a: AgentInfo) => a.activity.at(-1)?.text ?? a.summary ?? a.task;

/** The agent's open question, highlighted, with an answer field that sends to POST /agents/:id/answer. */
export function questionBox(a: AgentInfo, onDone: (next: AgentInfo) => void, compact = false): HTMLElement {
  const input = h('input', {
    class: 'input',
    placeholder: 'Answer…',
    'aria-label': `Answer: ${a.question?.text ?? ''}`,
  });
  const send = button(compact ? 'Answer' : [icon.send(14), 'Answer'], () => void go(), {
    size: compact ? 'sm' : undefined,
  });
  const go = () =>
    busy(send, async () => {
      const text = input.value.trim();
      if (!text) return input.focus();
      const next = await agentsApi.answer(a.id, text);
      toast('Answer sent');
      onDone(next);
    });
  input.addEventListener('keydown', (e) => (e as KeyboardEvent).key === 'Enter' && void go());
  return h(
    'div',
    { class: `question ${compact ? 'compact' : ''}`, 'data-question': a.question?.id ?? '' },
    h('div', { class: 'question-text' }, icon.help(16), h('span', {}, a.question?.text ?? '')),
    h('div', { class: 'inline-form' }, input, send),
  );
}
